/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DeviceModel } from "../device";
import { Log } from "../logging/Log";
import {
  WebSocketConnectionState,
  WebSocketObservableState,
  WebSocketFsmState,
  ConnectionTrigger,
  TransportEventType,
  MrpcMessageType,
  WebSocketErrorCode,
  WebSocketError
} from "./types";
import type {
  TransportEvent,
  ITransportOpenEvent,
  IWebSocketMessage,
  IWebSocketConfig,
  WebSocketStateListener,
  OperationHandler
} from "./types";
import { WebSocketJwtService } from "./WebSocketJwtService";
import { WebSocketTransport } from "./WebSocketTransport";
import { WebSocketMrpcv2Enveloper } from "./WebSocketMrpcv2Enveloper";
import { WebSocketSerializer } from "./WebSocketSerializer";
import { WebSocketPingManager } from "./WebSocketPingManager";
import { WebSocketLifecycleManager } from "./WebSocketLifecycleManager";
import { WebSocketCircuitBreaker } from "./WebSocketCircuitBreaker";

/**
 * WebSocket Manager — top-level orchestrator for the WebSocket Messaging 2.0 subsystem.
 *
 * Owns the transport, JWT authentication, message enveloping/serialization,
 * ping/keepalive, request routing, lifecycle management, and reconnection logic.
 *
 * **REQ-LIFE-01:** Exposes start/stop operations.
 * **REQ-LIFE-04:** No concurrent connection attempts.
 * **REQ-LIFE-06:** Observable connection state with replay.
 * **REQ-ROUTE-01 through REQ-ROUTE-06:** Operation handler registry.
 * **REQ-ERR-09:** Connection lost recovery.
 */
export class WebSocketManager {
  private static readonly TAG: string = "WebSocketManager";

  /**
   * GENERAL backoff policy (REQ-ERR-05a).
   *
   * | Group | Attempts | Base Interval | Jitter          |
   * |-------|----------|---------------|-----------------|
   * |   1   |  1–6     | 10 seconds    | None            |
   * |   2   |  7–12    | 5 minutes     | 0–5 min random  |
   * |   3   | 13+      | 10 minutes    | 0–5 min random  |
   *
   * No maximum — the app must keep retrying indefinitely (TV / long-running).
   */
  private static readonly GROUP1_MAX: number = 6;
  private static readonly GROUP2_MAX: number = 12;
  private static readonly GROUP1_BASE_MS: number = 10_000; // 10 seconds
  private static readonly GROUP2_BASE_MS: number = 300_000; // 5 minutes
  private static readonly GROUP3_BASE_MS: number = 600_000; // 10 minutes
  private static readonly JITTER_MAX_MS: number = 300_000; // 0–5 minutes

  /**
   * Watchdog timeout per FSM state processor (REQ-ERR-08).
   * If a state processor (delegate) does not complete within this window,
   * the operation is aborted with a WatchdogTimeout error.
   */
  private static readonly WATCHDOG_TIMEOUT_MS: number = 55_000; // 55 seconds

  /**
   * Connection open timeout (REQ-TRANS-10).
   * If the WebSocket does not fire the 'open' event within this window,
   * the connection is forcibly closed with code 3005.
   */
  private static readonly CONNECTION_TIMEOUT_MS: number = 10_000; // 10 seconds

  /** RFC 6455 normal closure (used in stop/destroy/hangup). */
  private static readonly CLOSE_NORMAL: number = 1000;

  /** Application-defined: connection open timeout (REQ-TRANS-10). */
  private static readonly CLOSE_CONNECTION_TIMEOUT: number = 3005;

  /**
   * Restartable FSM states (REQ-FSM-07).
   * Error in any of these triggers the RESTART flow.
   */
  private static readonly RESTARTABLE_STATES: ReadonlySet<WebSocketFsmState> =
    new Set([
      WebSocketFsmState.GET_CLIENT_CERT,
      WebSocketFsmState.GET_SECURE_ENDPOINT,
      WebSocketFsmState.CHECK_BRIDGEKEEPER,
      WebSocketFsmState.ESTABLISH_CONTEXT
    ]);

  private static readonly BUILT_IN_OPERATIONS: ReadonlySet<string> = new Set([
    "bodyConfigSearch",
    "notificationSend",
    "provisioningDataUpdateNotification",
    "emergencyAlertMessage"
  ]);

  /**
   * Unrecoverable WebSocket close codes (REQ-MON-05).
   * These indicate permanent failures — retrying is pointless.
   *
   * - 4003: Account Locked (server rejects the account permanently)
   */
  private static readonly UNRECOVERABLE_CLOSE_CODES: ReadonlySet<number> =
    new Set([4003]);

  /**
   * Check whether a WebSocket close code indicates an unrecoverable failure.
   * Unrecoverable → stop permanently (REQ-MON-05, REQ-ERR-06).
   * Recoverable → retry with backoff (REQ-MON-04, REQ-ERR-09).
   */
  private static isUnrecoverableClose(code: number): boolean {
    return WebSocketManager.UNRECOVERABLE_CLOSE_CODES.has(code);
  }

  /**
   * Compute the reconnection delay using the GENERAL backoff policy (REQ-ERR-05a).
   *
   * @param attempt - 1-based attempt number
   * @returns Delay in milliseconds (base + jitter)
   */
  private static computeBackoffDelay(attempt: number): number {
    let baseMs: number;
    let jitterMs: number;

    if (attempt <= WebSocketManager.GROUP1_MAX) {
      // Group 1: 10s, no jitter
      baseMs = WebSocketManager.GROUP1_BASE_MS;
      jitterMs = 0;
    } else if (attempt <= WebSocketManager.GROUP2_MAX) {
      // Group 2: 5 min + 0–5 min jitter
      baseMs = WebSocketManager.GROUP2_BASE_MS;
      // eslint-disable-next-line sonarjs/pseudo-random
      jitterMs = Math.floor(Math.random() * WebSocketManager.JITTER_MAX_MS);
    } else {
      // Group 3: 10 min + 0–5 min jitter (no cap — keeps retrying indefinitely)
      baseMs = WebSocketManager.GROUP3_BASE_MS;
      // eslint-disable-next-line sonarjs/pseudo-random
      jitterMs = Math.floor(Math.random() * WebSocketManager.JITTER_MAX_MS);
    }

    return baseMs + jitterMs;
  }

  private readonly mConfig: Required<
    Omit<IWebSocketConfig, "ensureCertificate" | "resolveEndpoint">
  >;
  private readonly mTransport: WebSocketTransport;
  private readonly mPingManager: WebSocketPingManager;
  private readonly mLifecycleManager: WebSocketLifecycleManager;

  /** Internal FSM state (REQ-FSM-01). */
  private mFsmState: WebSocketFsmState = WebSocketFsmState.IDLE;

  /** Observable state listeners (REQ-LIFE-06). */
  private mStateListeners: WebSocketStateListener[] = [];

  /** Operation handler registry (REQ-ROUTE-01). */
  private mHandlerRegistry: Map<string, OperationHandler> = new Map();

  /** External handler overrides for built-in operations. */
  private mExternalHandlerRegistry: Map<string, OperationHandler> = new Map();

  /** Current observable state. */
  private mObservableState: WebSocketObservableState =
    WebSocketObservableState.Idle;

  /** Last observable state detail string. */
  private mObservableDetail: string | undefined = undefined;

  /** Whether a connection attempt is in progress (REQ-LIFE-04). */
  private mIsConnecting: boolean = false;

  /** Current connection trigger. */
  private mCurrentTrigger: ConnectionTrigger | null = null;

  /** Reconnection state. */
  private mReconnectAttempts: number = 0;
  private mReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private mIsTimersSuspended: boolean = false;

  /** Circuit breaker for sustained failure detection (REQ-ERR-07). */
  private readonly mCircuitBreaker: WebSocketCircuitBreaker =
    new WebSocketCircuitBreaker();

  /** Connection open timeout timer (REQ-TRANS-10). */
  private mConnectionTimer: ReturnType<typeof setTimeout> | null = null;

  /** Whether the manager has been destroyed. */
  private mIsDestroyed: boolean = false;

  /** Monotonic counter for unique ping RpcIds. */
  private mPingSeq: number = 0;

  /** FSM GET_CLIENT_CERT delegate — ensures valid cert in storage. */
  private readonly mEnsureCertificate: (() => Promise<void>) | undefined;

  /** FSM GET_SECURE_ENDPOINT delegate — resolves fresh host/port. */
  private readonly mResolveEndpoint:
    | (() => Promise<{ host: string; port: number }>)
    | undefined;

  constructor(config: IWebSocketConfig) {
    this.mConfig = {
      host: config.host,
      port: config.port,
      path: config.path || "",
      subProtocol:
        config.subProtocol === undefined
          ? "com.tivo.mindrpc.2"
          : config.subProtocol,
      defaultPingIntervalSeconds:
        config.defaultPingIntervalSeconds === undefined
          ? 30
          : config.defaultPingIntervalSeconds,
      maxInboundMessageSize:
        config.maxInboundMessageSize === undefined
          ? 1048576
          : config.maxInboundMessageSize,
      outboundBufferCapacity:
        config.outboundBufferCapacity === undefined
          ? 8
          : config.outboundBufferCapacity,
      jwtValiditySeconds:
        config.jwtValiditySeconds === undefined
          ? 300
          : config.jwtValiditySeconds
    };

    // Store FSM state processor delegates (REQ-FSM-04)
    this.mEnsureCertificate = config.ensureCertificate;
    this.mResolveEndpoint = config.resolveEndpoint;

    this.mTransport = new WebSocketTransport(
      this.mConfig.maxInboundMessageSize,
      this.mConfig.outboundBufferCapacity
    );

    this.mPingManager = new WebSocketPingManager();
    this.mLifecycleManager = new WebSocketLifecycleManager();

    // Register transport event handler
    this.mTransport.addEventListener(this.handleTransportEvent.bind(this));

    // Register built-in protocol handlers
    this.registerBuiltInHandlers();

    Log.d(
      WebSocketManager.TAG,
      `constructor(): host=${this.mConfig.host}, port=${this.mConfig.port}, path=${this.mConfig.path}`
    );
  }

  /**
   * Transition the internal FSM to the next state (REQ-FSM-03).
   *
   * Validates that the transition is legal per the strict transition rules:
   * - START: only from IDLE or RESTART (CONNECT flow entry)
   * - GET_CLIENT_CERT: only from START
   * - GET_SECURE_ENDPOINT: only from GET_CLIENT_CERT
   * - CHECK_BRIDGEKEEPER: only from GET_SECURE_ENDPOINT
   * - ESTABLISH_CONTEXT: only from CHECK_BRIDGEKEEPER
   * - RESTART: only from a restartable state (REQ-FSM-07)
   * - STOP: from any active state (not IDLE)
   * - IDLE: only from STOP
   *
   * @param nextState - The target FSM state
   * @returns true if the transition was accepted, false if rejected
   */
  private transitionFsm(nextState: WebSocketFsmState): boolean {
    const current: WebSocketFsmState = this.mFsmState;
    let valid: boolean = false;

    switch (nextState) {
      case WebSocketFsmState.START:
        // CONNECT flow: only from IDLE or RESTART (REQ-FSM-02, REQ-FSM-03)
        valid =
          current === WebSocketFsmState.IDLE ||
          current === WebSocketFsmState.RESTART;
        break;
      case WebSocketFsmState.GET_CLIENT_CERT:
        valid = current === WebSocketFsmState.START;
        break;
      case WebSocketFsmState.GET_SECURE_ENDPOINT:
        valid = current === WebSocketFsmState.GET_CLIENT_CERT;
        break;
      case WebSocketFsmState.CHECK_BRIDGEKEEPER:
        valid = current === WebSocketFsmState.GET_SECURE_ENDPOINT;
        break;
      case WebSocketFsmState.ESTABLISH_CONTEXT:
        valid = current === WebSocketFsmState.CHECK_BRIDGEKEEPER;
        break;
      case WebSocketFsmState.RESTART:
        // Only from restartable states during CONNECT flow (REQ-FSM-03)
        valid = WebSocketManager.RESTARTABLE_STATES.has(current);
        break;
      case WebSocketFsmState.STOP:
        // STOP allowed from any non-IDLE state (REQ-FSM-06)
        valid = current !== WebSocketFsmState.IDLE;
        break;
      case WebSocketFsmState.IDLE:
        // IDLE only from STOP (REQ-FSM-03)
        valid = current === WebSocketFsmState.STOP;
        break;
    }

    if (!valid) {
      Log.e(
        WebSocketManager.TAG,
        `transitionFsm(): REJECTED illegal transition ${current} → ${nextState}`
      );
      return false;
    }

    Log.d(WebSocketManager.TAG, `transitionFsm(): ${current} → ${nextState}`);
    this.mFsmState = nextState;
    return true;
  }

  /**
   * Register built-in protocol handlers required by the server handshake.
   *
   * - `bodyConfigSearch`: Server sends this immediately after connection.
   *   Client must respond with supported operations (e.g. notificationSend).
   * - `notificationSend`: Server pushes notifications after authorization.
   * - `provisioningDataUpdateNotification`: Provisioning data updates.
   * - `emergencyAlertMessage`: EAS alert push from the server. Renamed from
   *   `emergencyAlertDisplayNotification` when the server aligned this
   *   dispatch RequestType with the wire body's own `requestType`
   *   self-description.
   */
  private registerBuiltInHandlers(): void {
    this.mHandlerRegistry.set(
      "bodyConfigSearch",
      this.handleBodyConfigSearch.bind(this)
    );
    this.mHandlerRegistry.set(
      "notificationSend",
      this.handleNotificationSend.bind(this)
    );
    this.mHandlerRegistry.set(
      "provisioningDataUpdateNotification",
      this.handleProvisioningDataUpdate.bind(this)
    );
    this.mHandlerRegistry.set(
      "emergencyAlertMessage",
      this.handleEmergencyAlertMessage.bind(this)
    );
    Log.d(
      WebSocketManager.TAG,
      "registerBuiltInHandlers(): Registered bodyConfigSearch, notificationSend, provisioningDataUpdateNotification, emergencyAlertMessage"
    );
  }

  /**
   * Handle `bodyConfigSearch` — server handshake request.
   *
   * Responds with a BodyConfigList containing the device bodyId, software version,
   * and the list of supported local operations (notificationSend).
   */
  private async handleBodyConfigSearch(
    message: IWebSocketMessage
  ): Promise<Record<string, unknown>> {
    Log.i(WebSocketManager.TAG, "handleBodyConfigSearch(): Received");

    // Notify external delegate (for logging/observation)
    const externalHandler: OperationHandler | undefined =
      this.mExternalHandlerRegistry.get("bodyConfigSearch");
    if (externalHandler !== undefined) {
      try {
        await externalHandler(message);
      } catch (error: unknown) {
        Log.w(
          WebSocketManager.TAG,
          "handleBodyConfigSearch(): External delegate error",
          error
        );
      }
    }

    let bodyId: string = "";
    try {
      const payload: Record<string, unknown> =
        WebSocketSerializer.deserialize(message);
      const rawBodyId: unknown = payload.bodyId;
      if (typeof rawBodyId === "string") {
        bodyId = rawBodyId;
      } else if (rawBodyId !== undefined && rawBodyId !== null) {
        bodyId = String(rawBodyId);
      }
    } catch {
      Log.w(
        WebSocketManager.TAG,
        "handleBodyConfigSearch(): Failed to parse bodyId from payload"
      );
    }

    return {
      type: "bodyConfigList",
      bodyConfig: [
        {
          type: "bodyConfig",
          bodyId: bodyId,
          softwareVersion: "new",
          supportedLocalOperation: ["notificationSend", "emergencyAlertMessage"]
        }
      ]
    };
  }

  /**
   * Handle `notificationSend` — server push notification.
   *
   * Delegates to the registered external handler if one exists,
   * otherwise logs and acknowledges.
   */
  private async handleNotificationSend(
    message: IWebSocketMessage
  ): Promise<Record<string, unknown> | null> {
    Log.i(
      WebSocketManager.TAG,
      `handleNotificationSend(): Received (bodyLen=${message.body.length})`
    );

    // Check if an external handler has been registered for notificationSend
    const externalHandler: OperationHandler | undefined =
      this.mExternalHandlerRegistry.get("notificationSend");
    if (externalHandler !== undefined) {
      return externalHandler(message);
    }

    // Default: log and return success acknowledgement
    try {
      const payload: Record<string, unknown> =
        WebSocketSerializer.deserialize(message);
      const rawType: unknown = payload.type;
      const typeForLog: string =
        rawType === undefined || rawType === null ? "unknown" : String(rawType);
      Log.d(
        WebSocketManager.TAG,
        `handleNotificationSend(): type=${typeForLog}`
      );
      Log.d(
        WebSocketManager.TAG,
        `handleNotificationSend(): ${JSON.stringify(payload)}`
      );
    } catch (error: unknown) {
      Log.w(
        WebSocketManager.TAG,
        "handleNotificationSend(): Failed to parse notification payload",
        error
      );
    }

    return { type: "success" };
  }

  /**
   * Handle `provisioningDataUpdateNotification` — provisioning data update.
   *
   * Delegates to external handler if registered, otherwise logs.
   */
  private async handleProvisioningDataUpdate(
    message: IWebSocketMessage
  ): Promise<Record<string, unknown> | null> {
    Log.i(
      WebSocketManager.TAG,
      `handleProvisioningDataUpdate(): Received (bodyLen=${message.body.length})`
    );

    const externalHandler: OperationHandler | undefined =
      this.mExternalHandlerRegistry.get("provisioningDataUpdateNotification");
    if (externalHandler !== undefined) {
      return externalHandler(message);
    }

    return { type: "success" };
  }

  /**
   * Handle `emergencyAlertMessage` — EAS alert push.
   *
   * Renamed from `handleEmergencyAlertDisplayNotification` alongside the
   * RequestType it services: the server aligned the MRPC/2 dispatch
   * RequestType with the wire body's own `requestType` self-description, so
   * both are now `emergencyAlertMessage`.
   *
   * Delegates to the registered external handler if one exists (e.g. to
   * surface an on-screen EAS alert), otherwise logs the alert payload and
   * acknowledges.
   */
  private async handleEmergencyAlertMessage(
    message: IWebSocketMessage
  ): Promise<Record<string, unknown> | null> {
    Log.i(
      WebSocketManager.TAG,
      `handleEmergencyAlertMessage(): Received (bodyLen=${message.body.length})`
    );

    const externalHandler: OperationHandler | undefined =
      this.mExternalHandlerRegistry.get("emergencyAlertMessage");
    if (externalHandler !== undefined) {
      return externalHandler(message);
    }

    try {
      const payload: Record<string, unknown> =
        WebSocketSerializer.deserialize(message);
      Log.d(
        WebSocketManager.TAG,
        `handleEmergencyAlertMessage(): ${JSON.stringify(payload)}`
      );
    } catch (error: unknown) {
      Log.w(
        WebSocketManager.TAG,
        "handleEmergencyAlertMessage(): Failed to parse EAS alert payload",
        error
      );
    }

    return { type: "success" };
  }

  /**
   * Run the FSM connect flow (REQ-FSM-02, REQ-FSM-08):
   *   START → GET_CLIENT_CERT → GET_SECURE_ENDPOINT →
   *   CHECK_BRIDGEKEEPER → ESTABLISH_CONTEXT
   *
   * Each phase is a discrete FSM state with validated transitions.
   * On error in a restartable state → RESTART flow with backoff.
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private async runConnectFlow(): Promise<void> {
    // REQ-ERR-07: Circuit breaker guard — skip if circuit is OPEN
    if (!this.mCircuitBreaker.allowAttempt()) {
      Log.w(
        WebSocketManager.TAG,
        `runConnectFlow(): Circuit breaker OPEN — skipping connect attempt`
      );
      this.mIsConnecting = false;
      this.setObservableState(
        WebSocketObservableState.Error,
        "Circuit breaker open — too many consecutive failures"
      );
      // Re-schedule to try again after cooldown
      this.scheduleReconnect();
      return;
    }

    try {
      // ── START (REQ-FSM-06: transient, synchronous, always succeeds) ──
      if (!this.transitionFsm(WebSocketFsmState.START)) {
        throw new WebSocketError(
          WebSocketErrorCode.TRANSPORT_ERROR,
          `Illegal FSM transition to START from ${this.mFsmState}`
        );
      }

      // ── GET_CLIENT_CERT (REQ-FSM-07: restartable) ──
      if (!this.transitionFsm(WebSocketFsmState.GET_CLIENT_CERT)) {
        throw new WebSocketError(
          WebSocketErrorCode.TRANSPORT_ERROR,
          `Illegal FSM transition to GET_CLIENT_CERT from ${this.mFsmState}`
        );
      }

      if (this.mIsDestroyed) {
        return;
      }

      // Delegate: ensure a valid cert is in storage (fetches fresh if needed)
      if (this.mEnsureCertificate !== undefined) {
        Log.d(
          WebSocketManager.TAG,
          "runConnectFlow(): GET_CLIENT_CERT — invoking ensureCertificate delegate"
        );
        await this.withWatchdog(
          this.mEnsureCertificate(),
          "GET_CLIENT_CERT:ensureCertificate"
        );
      }

      if (this.mIsDestroyed) {
        return;
      }

      // Read the (now-valid) certificate from DeviceModel
      Log.d(
        WebSocketManager.TAG,
        "runConnectFlow(): GET_CLIENT_CERT — reading certificate from storage"
      );
      const certificateData = await DeviceModel.getSoftClientCert();
      if (certificateData === null) {
        throw new WebSocketError(
          WebSocketErrorCode.CERTIFICATE_MISSING,
          "No soft client certificate found in storage"
        );
      }

      const { certificate, privateKey } = certificateData;

      if (!certificate) {
        throw new WebSocketError(
          WebSocketErrorCode.CERTIFICATE_MISSING,
          "Certificate is empty"
        );
      }
      if (!privateKey) {
        throw new WebSocketError(
          WebSocketErrorCode.PRIVATE_KEY_MISSING,
          "Private key is empty"
        );
      }

      // ── GET_SECURE_ENDPOINT (REQ-FSM-07: restartable) ──
      if (!this.transitionFsm(WebSocketFsmState.GET_SECURE_ENDPOINT)) {
        throw new WebSocketError(
          WebSocketErrorCode.TRANSPORT_ERROR,
          `Illegal FSM transition to GET_SECURE_ENDPOINT from ${this.mFsmState}`
        );
      }

      if (this.mIsDestroyed) {
        return;
      }

      // Delegate: resolve fresh WSS endpoint via TSRI
      let host: string = this.mConfig.host;
      let port: number = this.mConfig.port;

      if (this.mResolveEndpoint !== undefined) {
        Log.d(
          WebSocketManager.TAG,
          "runConnectFlow(): GET_SECURE_ENDPOINT — invoking resolveEndpoint delegate"
        );
        const resolved = await this.withWatchdog(
          this.mResolveEndpoint(),
          "GET_SECURE_ENDPOINT:resolveEndpoint"
        );
        host = resolved.host;
        port = resolved.port;
      }

      Log.d(
        WebSocketManager.TAG,
        `runConnectFlow(): GET_SECURE_ENDPOINT — endpoint ${host}:${port}`
      );

      // Warm up DNS + TCP + TLS for the WSS host before opening the WebSocket.
      // The TSRI endpoint is a different host than the WSS endpoint, so the
      // browser has never contacted it. Without this, the WebSocket upgrade
      // can fail silently on cold connections.
      // Only warm up on first connection attempt (cold start)
      if (this.mReconnectAttempts === 0) {
        try {
          await fetch(`https://${host}:${port}`, {
            method: "HEAD",
            mode: "no-cors"
          });
        } catch {
          /* best-effort */
        }
      }
      Log.d(
        WebSocketManager.TAG,
        `runConnectFlow(): DNS/TLS warmup complete for ${host}:${port}`
      );

      // ── CHECK_BRIDGEKEEPER (REQ-FSM-07: restartable, pass-through) ──
      if (!this.transitionFsm(WebSocketFsmState.CHECK_BRIDGEKEEPER)) {
        throw new WebSocketError(
          WebSocketErrorCode.TRANSPORT_ERROR,
          `Illegal FSM transition to CHECK_BRIDGEKEEPER from ${this.mFsmState}`
        );
      }
      Log.d(
        WebSocketManager.TAG,
        "runConnectFlow(): CHECK_BRIDGEKEEPER — not available on web, passing through"
      );
      // Bridgekeeper service is not yet available in the web codebase.

      // ── ESTABLISH_CONTEXT (REQ-FSM-07: restartable) ──
      if (!this.transitionFsm(WebSocketFsmState.ESTABLISH_CONTEXT)) {
        throw new WebSocketError(
          WebSocketErrorCode.TRANSPORT_ERROR,
          `Illegal FSM transition to ESTABLISH_CONTEXT from ${this.mFsmState}`
        );
      }

      if (this.mIsDestroyed) {
        return;
      }

      Log.d(
        WebSocketManager.TAG,
        "runConnectFlow(): ESTABLISH_CONTEXT — creating JWT and connecting"
      );

      // Create JWT (REQ-WEB-TRANS-02 §4.6.3: fresh JWT per connection attempt)
      const jwt: string = await this.withWatchdog(
        WebSocketJwtService.createJwt(
          certificate,
          privateKey,
          this.mConfig.jwtValiditySeconds
        ),
        "ESTABLISH_CONTEXT:createJwt"
      );

      // Build WSS URL (REQ-WEB-API-03)
      const encodedJwt: string = encodeURIComponent(jwt);
      const url: string = `wss://${host}:${port}${this.mConfig.path}?authorization=${encodedJwt}`;

      Log.d(
        WebSocketManager.TAG,
        `runConnectFlow(): ESTABLISH_CONTEXT — connecting to wss://${host}:${port}${this.mConfig.path}`
      );

      // Open WebSocket connection — result arrives via handleOpen/handleClosed
      this.mTransport.connect(url, this.mConfig.subProtocol);

      // REQ-TRANS-10: Start connection open timeout (10s)
      this.mConnectionTimer = setTimeout((): void => {
        this.mConnectionTimer = null;
        Log.w(
          WebSocketManager.TAG,
          `runConnectFlow(): Connection open timeout (${WebSocketManager.CONNECTION_TIMEOUT_MS}ms) — forcing close`
        );
        this.mTransport.close(
          WebSocketManager.CLOSE_CONNECTION_TIMEOUT,
          "Connection open timeout"
        );
      }, WebSocketManager.CONNECTION_TIMEOUT_MS);
    } catch (error: unknown) {
      Log.e(
        WebSocketManager.TAG,
        `runConnectFlow(): Error in FSM state ${this.mFsmState}`,
        error
      );
      this.mIsConnecting = false;

      const detail: string =
        error instanceof Error ? error.message : String(error);
      this.setObservableState(WebSocketObservableState.Error, detail);

      // REQ-ERR-07: Record failure with circuit breaker
      this.mCircuitBreaker.recordFailure();

      // REQ-FSM-07: Error in restartable state → RESTART flow
      if (WebSocketManager.RESTARTABLE_STATES.has(this.mFsmState)) {
        this.transitionFsm(WebSocketFsmState.RESTART);
        this.scheduleReconnect();
      }
    }
  }

  /**
   * Handle transport events from the WebSocket transport layer.
   */
  private handleTransportEvent(event: TransportEvent): void {
    switch (event.type) {
      case TransportEventType.Open:
        this.handleOpen(event as ITransportOpenEvent);
        break;
      case TransportEventType.TextMessage:
        this.handleTextMessage(event.data);
        break;
      case TransportEventType.Error:
        this.handleError(event.code, event.detail);
        break;
      case TransportEventType.Closed:
        this.handleClosed(event.code, event.detail);
        break;
      case TransportEventType.BinaryMessage:
        // Binary messages are converted to text and processed through the
        // same MRPCv2 pipeline (per reference spec).
        Log.d(
          WebSocketManager.TAG,
          `handleTransportEvent(): Binary message received (${event.length} bytes) — converting to text`
        );
        {
          const decoder: TextDecoder = new TextDecoder();
          const text: string = decoder.decode(event.data);
          this.handleTextMessage(text);
        }
        break;
    }
  }

  /**
   * Handle WebSocket connection open event.
   * FSM stays in ESTABLISH_CONTEXT — the connection is now established (REQ-FSM-05).
   */
  private handleOpen(event: ITransportOpenEvent): void {
    Log.i(
      WebSocketManager.TAG,
      `handleOpen(): Connected with protocol="${event.protocol}", FSM=${this.mFsmState}`
    );

    // Clear connection open timeout
    this.clearConnectionTimer();

    this.mIsConnecting = false;
    this.mReconnectAttempts = 0;

    // REQ-ERR-07: Record success with circuit breaker
    this.mCircuitBreaker.recordSuccess();

    // REQ-SER-05: Validate sub-protocol (warn only — browser may report
    // a different string depending on server negotiation)
    if (
      event.protocol !== this.mConfig.subProtocol &&
      event.protocol !== "" // Browser may report empty if server didn't negotiate
    ) {
      Log.w(
        WebSocketManager.TAG,
        `handleOpen(): Unexpected protocol "${event.protocol}", expected "${this.mConfig.subProtocol}"`
      );
    }

    // Start ping/keepalive
    this.mPingManager.setPingIntervalFromHeader(
      event.pingIntervalSeconds > 0
        ? String(event.pingIntervalSeconds)
        : undefined
    );
    this.mPingManager.start(
      () => this.handlePing(),
      () => this.handleHangup()
    );

    this.setObservableState(WebSocketObservableState.Connected);
  }

  /**
   * Handle incoming text message — unenvelope, route, and respond.
   */
  private handleTextMessage(rawFrame: string): void {
    // Record traffic for hangup detection
    this.mPingManager.recordTraffic();

    try {
      // Step 1: Unenvelope
      const message: IWebSocketMessage =
        WebSocketMrpcv2Enveloper.unenvelope(rawFrame);

      // Step 2: Route to handler (fire-and-forget to avoid blocking
      // the synchronous onmessage callback — prevents Violation warnings)
      this.routeMessage(message).catch((routeError: unknown): void => {
        Log.e(
          WebSocketManager.TAG,
          "handleTextMessage(): Routing failed",
          routeError
        );
      });
    } catch (error: unknown) {
      if (
        error instanceof WebSocketError &&
        error.code === WebSocketErrorCode.FEATURE_NOT_ENABLED
      ) {
        // FeatureNotEnabled errors (bad ResponseCount, empty RequestType)
        // always get an error response sent back if we can extract rpcId
        Log.w(
          WebSocketManager.TAG,
          `handleTextMessage(): FeatureNotEnabled — ${error.message}`
        );
        this.sendFeatureNotEnabledError(rawFrame, error.message);
      } else {
        // Bad argument (malformed frame) — no response sent (RpcId unreliable)
        Log.e(
          WebSocketManager.TAG,
          "handleTextMessage(): Failed to process message",
          error
        );
      }
    }
  }

  /**
   * Attempt to send a featureNotEnabled error response for a partially-parsed frame.
   */
  private sendFeatureNotEnabledError(rawFrame: string, detail: string): void {
    try {
      // Best-effort: try to extract RpcId and Origin-RequestId from headers
      const separatorIndex: number = rawFrame.indexOf("\r\n\r\n");
      if (separatorIndex === -1) {
        return;
      }
      const headerBlock: string = rawFrame.substring(0, separatorIndex);
      const lines: string[] = headerBlock.split("\r\n");
      let rpcId: string = "";
      let originRequestId: string = "";
      for (const line of lines) {
        if (line.startsWith("RpcId: ")) {
          rpcId = line.substring(7);
        } else if (line.startsWith("Origin-RequestId: ")) {
          originRequestId = line.substring(18);
        }
      }

      if (rpcId.length === 0 && originRequestId.length === 0) {
        return;
      }

      const errorMsg: IWebSocketMessage = WebSocketSerializer.serializeError(
        {
          type: MrpcMessageType.REQUEST,
          operationName: null,
          id: rpcId,
          originRequestId: originRequestId,
          headers: {},
          body: "",
          isResponseExpected: true
        },
        "featureNotEnabled",
        detail
      );
      const frame: string = WebSocketMrpcv2Enveloper.envelope(errorMsg);
      this.mTransport.send(frame);
    } catch (error: unknown) {
      Log.w(
        WebSocketManager.TAG,
        "sendFeatureNotEnabledError(): Failed to build error response",
        error
      );
    }
  }

  /**
   * Route an unenveloped message to the registered handler (REQ-ROUTE-03).
   */
  private async routeMessage(message: IWebSocketMessage): Promise<void> {
    const operationName: string | null = message.operationName;

    if (operationName === null || operationName.length === 0) {
      // featureNotEnabled: empty RequestType — always respond
      const errorMsg: IWebSocketMessage = WebSocketSerializer.serializeError(
        message,
        "featureNotEnabled",
        "Empty or missing RequestType"
      );
      const frame: string = WebSocketMrpcv2Enveloper.envelope(errorMsg);
      this.mTransport.send(frame);
      return;
    }

    const handler: OperationHandler | undefined =
      this.mHandlerRegistry.get(operationName);

    if (handler === undefined) {
      Log.w(
        WebSocketManager.TAG,
        `routeMessage(): No handler registered for "${operationName}"`
      );

      // routingError: no handler — always respond
      const errorMsg: IWebSocketMessage = WebSocketSerializer.serializeError(
        message,
        "routingError",
        `No registered handlers for: ${operationName}`
      );
      const frame: string = WebSocketMrpcv2Enveloper.envelope(errorMsg);
      this.mTransport.send(frame);
      return;
    }

    try {
      const responseObject: Record<string, unknown> | null =
        await handler(message);

      // REQ-ROUTE-04: Send response if expected
      if (message.isResponseExpected) {
        if (responseObject !== null) {
          const responseMessage: IWebSocketMessage =
            WebSocketSerializer.serialize(message, responseObject);
          const frame: string =
            WebSocketMrpcv2Enveloper.envelope(responseMessage);
          this.mTransport.send(frame);
        } else {
          // Null handler response with isResponseExpected=true → internalError
          // (WEBSOCKET_MESSAGE_PROCESSING §6: null client response → ErrorCode.internalError)
          Log.w(
            WebSocketManager.TAG,
            `routeMessage(): Handler for "${operationName}" returned null but response is expected — sending internalError`
          );
          const errorMsg: IWebSocketMessage =
            WebSocketSerializer.serializeError(
              message,
              "internalError",
              `Handler returned null for: ${operationName}`
            );
          const frame: string = WebSocketMrpcv2Enveloper.envelope(errorMsg);
          this.mTransport.send(frame);
        }
      }
    } catch (error: unknown) {
      Log.e(
        WebSocketManager.TAG,
        `routeMessage(): Handler for "${operationName}" threw`,
        error
      );

      // internalError: handler failure — always respond
      const errorMsg: IWebSocketMessage = WebSocketSerializer.serializeError(
        message,
        "internalError",
        `Handler error: ${error instanceof Error ? error.message : String(error)}`
      );
      const frame: string = WebSocketMrpcv2Enveloper.envelope(errorMsg);
      try {
        this.mTransport.send(frame);
      } catch (error: unknown) {
        Log.w(
          WebSocketManager.TAG,
          `routeMessage(): Failed to send error response for "${operationName}" — connection may be closed`,
          error
        );
      }
    }
  }

  /**
   * Handle transport error.
   */
  private handleError(code: string, detail: string): void {
    Log.e(
      WebSocketManager.TAG,
      `handleError(): code=${code}, detail="${detail}"`
    );
  }

  /**
   * Handle transport close — initiate restart if in a restartable FSM state (REQ-ERR-09, REQ-FSM-07).
   * Unrecoverable close codes (e.g. 4003 Account Locked) → stop permanently (REQ-MON-05).
   */
  private handleClosed(code: number, detail: string): void {
    Log.i(
      WebSocketManager.TAG,
      `handleClosed(): code=${code}, detail="${detail}", FSM=${this.mFsmState}`
    );

    // Clear connection open timeout
    this.clearConnectionTimer();

    this.mPingManager.stop();
    this.mIsConnecting = false;

    // If we were intentionally stopped or destroyed, don't reconnect
    if (
      this.mIsDestroyed ||
      this.mObservableState === WebSocketObservableState.Idle
    ) {
      return;
    }

    // REQ-MON-05: Unrecoverable close code → stop permanently
    if (WebSocketManager.isUnrecoverableClose(code)) {
      Log.w(
        WebSocketManager.TAG,
        `handleClosed(): Unrecoverable close code ${code} — stopping permanently`
      );
      this.setObservableState(
        WebSocketObservableState.Error,
        `Unrecoverable: ${detail} (code ${code})`
      );
      if (this.mFsmState !== WebSocketFsmState.IDLE) {
        this.transitionFsm(WebSocketFsmState.STOP);
        this.transitionFsm(WebSocketFsmState.IDLE);
      }
      return;
    }

    this.setObservableState(WebSocketObservableState.Error, detail);

    // REQ-ERR-07: Record failure with circuit breaker
    this.mCircuitBreaker.recordFailure();

    // REQ-FSM-07 / REQ-ERR-09: Restartable state → RESTART → reconnect
    if (WebSocketManager.RESTARTABLE_STATES.has(this.mFsmState)) {
      this.transitionFsm(WebSocketFsmState.RESTART);
    }

    this.scheduleReconnect();
  }

  /**
   * Handle keepalive ping timer tick (REQ-WEB-TRANS-06).
   *
   * Sends a minimal MRPCv2 request frame every 30 seconds with
   * ResponseCount: none so the server does not reply.
   *
   * This serves two purposes:
   * 1. If the TCP pipe is broken, the send triggers onerror/onclose
   *    immediately — faster than waiting for TCP keepalive timeout.
   * 2. Only real **inbound** server messages extend the hangup deadline
   *    (via recordTraffic in handleTextMessage). If no server traffic
   *    arrives within 40s, handleHangup closes the connection.
   *
   * We do NOT call recordTraffic() here — outbound pings must not
   * mask a silent/dead server.
   */
  private handlePing(): void {
    if (this.mTransport.getState() !== WebSocketConnectionState.CONNECTED) {
      return;
    }

    try {
      this.mPingManager.recordTraffic();
      Log.d(WebSocketManager.TAG, "handlePing(): Traffic timestamp refreshed");
    } catch {
      Log.w(
        WebSocketManager.TAG,
        "handlePing(): Failed to send ping — connection may be dead"
      );
    }
  }

  /**
   * Handle hangup timeout — no traffic received within deadline.
   */
  private handleHangup(): void {
    Log.w(
      WebSocketManager.TAG,
      "handleHangup(): No traffic within hangup deadline — closing connection"
    );
    this.mTransport.close(WebSocketManager.CLOSE_NORMAL, "Hangup timeout");
  }

  /**
   * Schedule a reconnection attempt using the GENERAL backoff policy (REQ-ERR-05a, REQ-FSM-08).
   *
   * No maximum attempt cap — the app retries indefinitely to support
   * long-running deployments (TV / kiosk) and sustained server outages.
   * Jitter in groups 2+ prevents thundering-herd reconnections across
   * multiple browser instances (e.g. LG webOS iframes).
   */
  private scheduleReconnect(): void {
    if (this.mIsDestroyed) {
      return;
    }

    if (this.mIsTimersSuspended) {
      Log.d(
        WebSocketManager.TAG,
        "scheduleReconnect(): Timers suspended — will reconnect on resume"
      );
      return;
    }

    this.mReconnectAttempts++;
    const delay: number = WebSocketManager.computeBackoffDelay(
      this.mReconnectAttempts
    );
    let group: number;
    if (this.mReconnectAttempts <= WebSocketManager.GROUP1_MAX) {
      group = 1;
    } else if (this.mReconnectAttempts <= WebSocketManager.GROUP2_MAX) {
      group = 2;
    } else {
      group = 3;
    }

    Log.d(
      WebSocketManager.TAG,
      `scheduleReconnect(): Attempt ${this.mReconnectAttempts} (group ${group}) in ${Math.round(delay / 1000)}s, FSM=${this.mFsmState}`
    );

    this.setObservableState(WebSocketObservableState.Reconnecting);

    this.mReconnectTimer = setTimeout(async (): Promise<void> => {
      this.mReconnectTimer = null;
      this.mIsConnecting = true;
      this.setObservableState(WebSocketObservableState.Connecting);
      await this.runConnectFlow();
    }, delay);
  }

  /**
   * Cancel any pending reconnection timer.
   */
  private cancelReconnect(): void {
    if (this.mReconnectTimer !== null) {
      clearTimeout(this.mReconnectTimer);
      this.mReconnectTimer = null;
    }
  }

  /**
   * Lifecycle callback: reconnect triggered by visibility or network event (REQ-FSM-08).
   *
   * If currently connected or already connecting, this is a no-op.
   * If in RESTART state (waiting), immediately re-enter the connect flow.
   * Otherwise enter RESTART → connect flow.
   */
  private handleLifecycleConnect(trigger: ConnectionTrigger): void {
    if (
      this.mTransport.getState() === WebSocketConnectionState.CONNECTED ||
      this.mIsConnecting
    ) {
      return;
    }

    Log.d(
      WebSocketManager.TAG,
      `handleLifecycleConnect(): Reconnecting due to ${trigger}, FSM=${this.mFsmState}`
    );
    this.mReconnectAttempts = 0;
    this.mIsConnecting = true;
    this.mCurrentTrigger = trigger;

    // REQ-ERR-07: Explicit user/network trigger resets the circuit breaker
    this.mCircuitBreaker.reset();

    this.setObservableState(WebSocketObservableState.Connecting);

    // If we're in a restartable state, transition to RESTART first
    if (
      WebSocketManager.RESTARTABLE_STATES.has(this.mFsmState) &&
      this.mFsmState !== WebSocketFsmState.RESTART
    ) {
      this.transitionFsm(WebSocketFsmState.RESTART);
    }

    this.runConnectFlow().catch((error: unknown): void => {
      Log.e(
        WebSocketManager.TAG,
        "handleLifecycleConnect(): Unhandled error in connect flow",
        error
      );
    });
  }

  /**
   * Lifecycle callback: suspend retry timers (REQ-WEB-LIFE-05).
   */
  private handleLifecycleSuspend(): void {
    Log.d(
      WebSocketManager.TAG,
      "handleLifecycleSuspend(): Suspending retry timers"
    );
    this.mIsTimersSuspended = true;
    this.cancelReconnect();
  }

  /**
   * Lifecycle callback: resume retry timers.
   */
  private handleLifecycleResume(): void {
    Log.d(
      WebSocketManager.TAG,
      "handleLifecycleResume(): Resuming retry timers"
    );
    this.mIsTimersSuspended = false;
  }

  /**
   * Update the observable state and notify all listeners.
   */
  private setObservableState(
    state: WebSocketObservableState,
    detail?: string
  ): void {
    if (this.mObservableState === state && this.mObservableDetail === detail) {
      return;
    }

    const suffix: string = detail !== undefined ? " (" + detail + ")" : "";
    Log.d(
      WebSocketManager.TAG,
      `setObservableState(): ${this.mObservableState} → ${state}${suffix}`
    );

    this.mObservableState = state;
    this.mObservableDetail = detail;

    for (const listener of this.mStateListeners) {
      try {
        listener(state, detail);
      } catch (error: unknown) {
        Log.e(
          WebSocketManager.TAG,
          "setObservableState(): Listener threw",
          error
        );
      }
    }
  }

  /**
   * Wrap an async operation with a watchdog timeout (REQ-ERR-08).
   *
   * If the operation does not resolve within WATCHDOG_TIMEOUT_MS (55s),
   * rejects with a WATCHDOG_TIMEOUT error that triggers the RESTART flow.
   */
  private withWatchdog<T>(operation: Promise<T>, label: string): Promise<T> {
    return new Promise<T>((resolve, reject): void => {
      let settled: boolean = false;

      const timer: ReturnType<typeof setTimeout> = setTimeout((): void => {
        if (!settled) {
          settled = true;
          Log.w(
            WebSocketManager.TAG,
            `withWatchdog(): ${label} timed out after ${WebSocketManager.WATCHDOG_TIMEOUT_MS}ms`
          );
          reject(
            new WebSocketError(
              WebSocketErrorCode.WATCHDOG_TIMEOUT,
              `Watchdog timeout in ${label} (${WebSocketManager.WATCHDOG_TIMEOUT_MS}ms)`
            )
          );
        }
      }, WebSocketManager.WATCHDOG_TIMEOUT_MS);

      operation.then(
        (value: T): void => {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            resolve(value);
          }
        },
        (error: unknown): void => {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            reject(error);
          }
        }
      );
    });
  }

  /**
   * Clear the connection open timeout timer.
   */
  private clearConnectionTimer(): void {
    if (this.mConnectionTimer !== null) {
      clearTimeout(this.mConnectionTimer);
      this.mConnectionTimer = null;
    }
  }

  /**
   * Start a connection attempt with the given trigger (REQ-LIFE-01).
   *
   * @param trigger - The named reason for connection initiation
   */
  public async start(trigger: ConnectionTrigger): Promise<void> {
    if (this.mIsDestroyed) {
      Log.w(WebSocketManager.TAG, "start(): Manager is destroyed — ignoring");
      return;
    }

    // REQ-LIFE-04: No concurrent attempts
    if (this.mIsConnecting) {
      Log.w(
        WebSocketManager.TAG,
        `start(): Connection attempt already in progress (trigger: ${this.mCurrentTrigger}) — ignoring ${trigger}`
      );
      return;
    }

    // If already connected, ignore unless it's a specific re-trigger
    if (this.mTransport.getState() === WebSocketConnectionState.CONNECTED) {
      Log.d(
        WebSocketManager.TAG,
        `start(): Already connected — ignoring trigger ${trigger}`
      );
      return;
    }

    Log.i(
      WebSocketManager.TAG,
      `start(): Initiating connection with trigger=${trigger}, FSM=${this.mFsmState}`
    );

    this.mCurrentTrigger = trigger;
    this.mIsConnecting = true;
    this.setObservableState(WebSocketObservableState.Connecting);

    // Start lifecycle listeners
    this.mLifecycleManager.start(
      this.handleLifecycleConnect.bind(this),
      this.handleLifecycleSuspend.bind(this),
      this.handleLifecycleResume.bind(this)
    );

    await this.runConnectFlow();
  }

  /**
   * Stop the connection and all subsystems (REQ-LIFE-03, REQ-FSM-06).
   *
   * Teardown (transport close, timer cancellation) is completed BEFORE entering
   * the STOP state per REQ-FSM-06: STOP processor MUST NOT perform network I/O.
   */
  public stop(): void {
    Log.i(
      WebSocketManager.TAG,
      `stop(): Stopping WebSocket subsystem, FSM=${this.mFsmState}`
    );

    // Teardown BEFORE entering STOP (REQ-FSM-06)
    this.cancelReconnect();
    this.clearConnectionTimer();
    this.mPingManager.stop();
    this.mLifecycleManager.stop();

    if (
      this.mTransport.getState() === WebSocketConnectionState.CONNECTED ||
      this.mTransport.getState() === WebSocketConnectionState.CONNECTING
    ) {
      this.mTransport.close(WebSocketManager.CLOSE_NORMAL, "Normal closure");
    }

    this.mIsConnecting = false;
    this.mCurrentTrigger = null;
    this.mReconnectAttempts = 0;

    // FSM: → STOP → IDLE (REQ-FSM-06: transient, always succeeds)
    if (this.mFsmState !== WebSocketFsmState.IDLE) {
      this.transitionFsm(WebSocketFsmState.STOP);
      this.transitionFsm(WebSocketFsmState.IDLE);
    }

    this.setObservableState(WebSocketObservableState.Idle);
  }

  /**
   * Send a raw MRPCv2 frame over the active connection (REQ-TRANS-04).
   *
   * @param frame - The enveloped MRPCv2 frame string
   * @throws WebSocketError if not connected
   */
  public sendFrame(frame: string): void {
    this.mTransport.send(frame);
  }

  /**
   * Send a typed response object for an inbound request.
   *
   * Serializes and envelopes the response before transmission.
   *
   * @param originalMessage - The inbound request being responded to
   * @param responseObject - The typed response payload
   */
  public sendResponse(
    originalMessage: IWebSocketMessage,
    responseObject: Record<string, unknown>
  ): void {
    const responseMessage: IWebSocketMessage = WebSocketSerializer.serialize(
      originalMessage,
      responseObject
    );
    const frame: string = WebSocketMrpcv2Enveloper.envelope(responseMessage);
    this.mTransport.send(frame);
  }

  /**
   * Register an operation handler (REQ-ROUTE-01).
   *
   * For built-in operations (bodyConfigSearch, notificationSend,
   * provisioningDataUpdateNotification), the handler is stored as an
   * external delegate invoked by the built-in handler.
   *
   * @param operationName - The operation type to handle
   * @param handler - The handler function
   */
  public registerHandler(
    operationName: string,
    handler: OperationHandler
  ): void {
    if (WebSocketManager.BUILT_IN_OPERATIONS.has(operationName)) {
      this.mExternalHandlerRegistry.set(operationName, handler);
    } else {
      this.mHandlerRegistry.set(operationName, handler);
    }
    Log.d(
      WebSocketManager.TAG,
      `registerHandler(): Registered handler for "${operationName}"`
    );
  }

  /**
   * Deregister an operation handler (REQ-ROUTE-02).
   *
   * @param operationName - The operation type to deregister
   */
  public deregisterHandler(operationName: string): void {
    if (WebSocketManager.BUILT_IN_OPERATIONS.has(operationName)) {
      this.mExternalHandlerRegistry.delete(operationName);
    } else {
      this.mHandlerRegistry.delete(operationName);
    }
    Log.d(
      WebSocketManager.TAG,
      `deregisterHandler(): Removed handler for "${operationName}"`
    );
  }

  /**
   * Subscribe to observable connection state changes (REQ-LIFE-06).
   * The current state is replayed immediately to the new listener.
   *
   * @param listener - State change callback
   */
  public addStateListener(listener: WebSocketStateListener): void {
    this.mStateListeners.push(listener);
    // Replay current state
    listener(this.mObservableState);
  }

  /**
   * Unsubscribe from state changes.
   */
  public removeStateListener(listener: WebSocketStateListener): void {
    this.mStateListeners = this.mStateListeners.filter((l) => l !== listener);
  }

  /**
   * Get the current observable state.
   */
  public getState(): WebSocketObservableState {
    return this.mObservableState;
  }

  /**
   * Get the underlying transport state.
   */
  public getTransportState(): WebSocketConnectionState {
    return this.mTransport.getState();
  }

  /**
   * Get the current internal FSM state (REQ-FSM-01).
   */
  public getFsmState(): WebSocketFsmState {
    return this.mFsmState;
  }

  /**
   * Destroy the manager and release all resources (REQ-ROUTE-06).
   */
  public destroy(): void {
    Log.i(WebSocketManager.TAG, "destroy(): Destroying WebSocket subsystem");
    this.mIsDestroyed = true;
    this.stop();
    this.mTransport.destroy();
    this.mHandlerRegistry.clear();
    this.mExternalHandlerRegistry.clear();
    this.mStateListeners = [];
  }
}
